import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './GasDistributorEntregaImediataConfigModal.css';
import AppIcon from './icons/AppIcon';

/**
 * Modal de configuração específico para a lista "Entrega Imediata"
 * Permite configurar notificação para grupo e cliente quando pedido é criado
 */
export default function GasDistributorEntregaImediataConfigModal({ list, onClose, onSave, projectId }) {
  const [loading, setLoading] = useState(false);
  const [grupoConfigurado, setGrupoConfigurado] = useState(null);
  const [conexaoConfigurada, setConexaoConfigurada] = useState(null);
  
  // Notificação para Grupo
  const [notificacaoAtivada, setNotificacaoAtivada] = useState(false);
  const [mensagemPersonalizada, setMensagemPersonalizada] = useState('');
  const [botoesAtivados, setBotoesAtivados] = useState(true);
  
  // Notificação para Cliente
  const [notificarCliente, setNotificarCliente] = useState(false);
  const [mensagemCliente, setMensagemCliente] = useState('');

  // Carregar configurações do projeto (conexão e grupo)
  useEffect(() => {
    const loadProjectConfig = async () => {
      try {
        const effectiveProjectId = projectId || list?.project_id;
        
        if (!effectiveProjectId) {
          console.error('projectId não disponível');
          return;
        }

        // Buscar projeto com settings
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('settings')
          .eq('id', effectiveProjectId)
          .maybeSingle();

        if (projectError) {
          console.error('Erro ao buscar projeto:', projectError);
        }

        // Buscar conexão vinculada ao projeto
        const connectionId = project?.settings?.gas_distributor?.connection_id;
        if (connectionId) {
          const { data: connection, error: connError } = await supabase
            .from('connections')
            .select('id, name, number')
            .eq('id', connectionId)
            .maybeSingle();

          if (connError) {
            console.error('Erro ao buscar conexão:', connError);
          } else if (connection) {
            setConexaoConfigurada(connection);
          }
        }

        // Buscar config do gas_distributor (grupo)
        const { data: config, error } = await supabase
          .from('gas_distributor_config')
          .select('grupo_whatsapp_id, grupo_whatsapp_nome')
          .eq('project_id', effectiveProjectId)
          .maybeSingle();

        if (error) {
          console.error('Erro ao buscar configuração do grupo:', error);
          return;
        }

        if (config?.grupo_whatsapp_id) {
          setGrupoConfigurado({
            id: config.grupo_whatsapp_id,
            nome: config.grupo_whatsapp_nome || 'Grupo configurado'
          });
        }
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };

    // Carregar configuração da lista
    const loadListConfig = async () => {
      try {
        const { data: listData, error } = await supabase
          .from('project_lists')
          .select('metadata')
          .eq('id', list.id)
          .maybeSingle();

        if (error) throw error;

        const metadata = listData?.metadata || {};
        const groupNotification = metadata.group_notification || {};
        
        setNotificacaoAtivada(groupNotification.enabled || false);
        setMensagemPersonalizada(groupNotification.message || '');
        setBotoesAtivados(groupNotification.buttons_enabled !== false); // Default true
        setNotificarCliente(groupNotification.notify_client || false);
        setMensagemCliente(groupNotification.client_message || '');
      } catch (error) {
        console.error('Erro ao carregar configuração da lista:', error);
      }
    };

    loadProjectConfig();
    if (list?.id) {
      loadListConfig();
    }
  }, [projectId, list?.id, list?.project_id]);

  const handleSave = async () => {
    try {
      setLoading(true);

      const metadata = list?.metadata || {};
      const updatedMetadata = {
        ...metadata,
        group_notification: {
          enabled: notificacaoAtivada,
          message: mensagemPersonalizada,
          buttons_enabled: botoesAtivados,
          notify_client: notificarCliente,
          client_message: mensagemCliente
        }
      };

      const { error } = await supabase
        .from('project_lists')
        .update({
          metadata: updatedMetadata,
          updated_at: new Date().toISOString()
        })
        .eq('id', list.id);

      if (error) throw error;

      alert('Configurações salvas com sucesso!');
      onSave({ ...list, metadata: updatedMetadata });
      onClose();
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="imediata-modal-overlay" onClick={onClose}>
      <div className="imediata-config-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="imediata-modal-header">
          <div className="imediata-modal-title-section">
            <AppIcon name="bolt" />
            <div>
              <h2>Configurar</h2>
              <p className="list-name">{list?.title}</p>
            </div>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="imediata-modal-content">
          {/* Seção de Notificação para Grupo */}
          <div className="imediata-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="group" />
                <h4>Notificação Grupo WhatsApp</h4>
              </div>
            </div>

            {/* Grupo Configurado */}
            <div className="grupo-info-box">
              <div className="grupo-info-header">
                <AppIcon name="groups" className="grupo-icon" />
                <div className="grupo-info-content">
                  <span className="grupo-label">Grupo Configurado no Projeto</span>
                  {grupoConfigurado ? (
                    <>
                      <span className="grupo-nome">{grupoConfigurado.nome}</span>
                      <span className="grupo-id">{grupoConfigurado.id}</span>
                    </>
                  ) : (
                    <span className="grupo-nao-configurado">Nenhum grupo configurado</span>
                  )}
                </div>
              </div>
            </div>

            {/* Conexão Configurada */}
            <div className="conexao-info-box">
              <div className="conexao-info-header">
                <AppIcon name="link" className="conexao-icon" />
                <div className="conexao-info-content">
                  <span className="conexao-label">Conexão Vinculada ao Projeto</span>
                  {conexaoConfigurada ? (
                    <>
                      <span className="conexao-nome">{conexaoConfigurada.name}</span>
                      <span className="conexao-numero">{conexaoConfigurada.number}</span>
                    </>
                  ) : (
                    <span className="conexao-nao-configurada">Nenhuma conexão vinculada</span>
                  )}
                </div>
              </div>
            </div>

            {/* Toggle de Ativação */}
            <div className="tool-enable-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={notificacaoAtivada}
                  onChange={(e) => setNotificacaoAtivada(e.target.checked)}
                  disabled={!grupoConfigurado}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {notificacaoAtivada 
                    ? '✅ Notificação ativada' 
                    : '❌ Notificação desativada'}
                </span>
              </label>
              <small className="toggle-description">
                {notificacaoAtivada 
                  ? 'Quando um novo pedido for criado, uma mensagem será enviada automaticamente para o grupo'
                  : 'Nenhuma mensagem será enviada automaticamente'}
              </small>
            </div>

            {/* Opções quando notificação ativada */}
            {notificacaoAtivada && (
              <>
                {/* Toggle de Botões */}
                <div className="tool-enable-section">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={botoesAtivados}
                      onChange={(e) => setBotoesAtivados(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">
                      {botoesAtivados 
                        ? '🔘 Botões ativados' 
                        : '❌ Sem botões'}
                    </span>
                  </label>
                  <small className="toggle-description">
                    {botoesAtivados 
                      ? 'A mensagem incluirá botões "Saiu para Entrega" e "Entregue" para atualizar o status rapidamente'
                      : 'A mensagem será enviada sem botões de ação'}
                  </small>
                </div>

                {/* Mensagem Personalizada */}
                <div className="mensagem-section">
                  <label className="mensagem-label">Mensagem para Grupo (opcional)</label>
                  <textarea
                    value={mensagemPersonalizada}
                    onChange={(e) => setMensagemPersonalizada(e.target.value)}
                    placeholder="🚚 Novo Pedido - Entrega Imediata&#10;&#10;👤 Cliente: {cliente}&#10;&#10;📍 Endereço: {endereco}&#10;&#10;🛒 Produto: {produto}&#10;&#10;📦 Quantidade: {quantidade}&#10;&#10;💰 Total: R$ {total}&#10;&#10;💳 Pagamento: {forma_pagamento}"
                    rows={8}
                    className="mensagem-textarea"
                  />
                  <small className="mensagem-hint">
                    Variáveis: {'{cliente}'}, {'{endereco}'}, {'{produto}'}, {'{quantidade}'}, {'{total}'}, {'{forma_pagamento}'}
                  </small>
                </div>
              </>
            )}
          </div>

          {/* Seção de Notificação para Cliente */}
          <div className="imediata-section client-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="person" />
                <h4>Notificação para o Cliente</h4>
              </div>
            </div>

            {/* Toggle de Ativação para Cliente */}
            <div className="tool-enable-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={notificarCliente}
                  onChange={(e) => setNotificarCliente(e.target.checked)}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {notificarCliente 
                    ? '✅ Notificar cliente no privado' 
                    : '❌ Não notificar cliente'}
                </span>
              </label>
              <small className="toggle-description">
                {notificarCliente 
                  ? 'Uma mensagem será enviada no privado para o cliente confirmando o pedido'
                  : 'Nenhuma mensagem será enviada para o cliente'}
              </small>
            </div>

            {/* Mensagem para Cliente */}
            {notificarCliente && (
              <div className="mensagem-section">
                <label className="mensagem-label">Mensagem para Cliente (opcional)</label>
                <textarea
                  value={mensagemCliente}
                  onChange={(e) => setMensagemCliente(e.target.value)}
                  placeholder="✅ *Pedido Confirmado!*&#10;&#10;📦 Produto: {produto}&#10;📊 Quantidade: {quantidade} unidade(s)&#10;💵 Total: R$ {total}&#10;&#10;⏱️ Previsão de entrega: {tempo_entrega} minutos&#10;&#10;Obrigado pela preferência! 🙏"
                  rows={6}
                  className="mensagem-textarea"
                />
                <small className="mensagem-hint">
                  Variáveis: {'{cliente}'}, {'{produto}'}, {'{quantidade}'}, {'{total}'}, {'{tempo_entrega}'}
                </small>
              </div>
            )}
          </div>

          {/* Informações sobre a lista */}
          <div className="imediata-section info-section">
            <h4>
              <AppIcon name="info" />
              Sobre esta lista
            </h4>
            <p className="info-text">
              A lista <strong>"Entrega Imediata"</strong> recebe pedidos confirmados pelo agente de IA 
              dentro do horário de funcionamento. Configure as notificações para manter a equipe informada.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="imediata-modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="btn-save" 
            onClick={handleSave}
            disabled={loading}
          >
            <AppIcon name="check" />
            {loading ? 'Salvando...' : 'Salvar Configuração'}
          </button>
        </div>
      </div>
    </div>
  );
}
